import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Button from '../components/Button';
import AdminPage from '../components/AdminShell';

const COLOR_PRESETS = [
  { label: 'Gray',   hex: '#F5F5F526' },
  { label: 'Purple', hex: '#7C3AED26' },
  { label: 'Green',  hex: '#16A34A26' },
  { label: 'Orange', hex: '#EA580C26' },
  { label: 'Teal',   hex: '#0D948826' },
  { label: 'Blue',   hex: '#4D806426' },
];

const AUDIENCE_OPTIONS = [
  { value: 'all',     label: 'All' },
  { value: 'coach',   label: 'Coach' },
  { value: 'player',  label: 'Player' },
  { value: 'partner', label: 'Partner' },
];

const EMPTY_FORM = {
  title: '',
  preview: '',
  detail: '',
  color: '#F5F5F526',
  target_audience: 'all',
  sort_order: 0,
  is_active: true,
};

export default function AdminContentPage() {
  const { API_URL } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [mediaFile, setMediaFile] = useState(null);
  const [removeMedia, setRemoveMedia] = useState(false);
  const [saving, setSaving] = useState(false);

  const getConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const showToast = (msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const fetchCards = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/content/admin`, getConfig());
      setCards(res.data);
    } catch (err) {
      showToast('Failed to load content cards', 'error');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchCards(); }, [fetchCards]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, sort_order: cards.length });
    setMediaFile(null);
    setRemoveMedia(false);
    setShowModal(true);
  };

  const openEdit = (card) => {
    setEditingId(card.id);
    setForm({
      title: card.title,
      preview: card.preview,
      detail: card.detail,
      color: card.color,
      target_audience: card.target_audience,
      sort_order: card.sort_order,
      is_active: card.is_active,
    });
    setMediaFile(null);
    setRemoveMedia(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.preview.trim() || !form.detail.trim()) {
      showToast('Title, preview, and detail are required', 'error');
      return;
    }

    setSaving(true);
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('preview', form.preview);
    fd.append('detail', form.detail);
    fd.append('color', form.color);
    fd.append('target_audience', form.target_audience);
    fd.append('sort_order', form.sort_order);
    fd.append('is_active', form.is_active);
    if (mediaFile) fd.append('media', mediaFile);
    if (removeMedia) fd.append('remove_media', 'true');

    try {
      if (editingId) {
        await axios.put(`${API_URL}/content/${editingId}`, fd, {
          headers: { ...getConfig().headers, 'Content-Type': 'multipart/form-data' },
        });
        showToast('Content card updated');
      } else {
        await axios.post(`${API_URL}/content`, fd, {
          headers: { ...getConfig().headers, 'Content-Type': 'multipart/form-data' },
        });
        showToast('Content card created');
      }
      closeModal();
      fetchCards();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this content card? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/content/${id}`, getConfig());
      showToast('Content card deleted');
      fetchCards();
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  const toggleActive = async (card) => {
    const fd = new FormData();
    fd.append('title', card.title);
    fd.append('preview', card.preview);
    fd.append('detail', card.detail);
    fd.append('color', card.color);
    fd.append('target_audience', card.target_audience);
    fd.append('sort_order', card.sort_order);
    fd.append('is_active', !card.is_active);

    try {
      await axios.put(`${API_URL}/content/${card.id}`, fd, {
        headers: { ...getConfig().headers, 'Content-Type': 'multipart/form-data' },
      });
      fetchCards();
    } catch (err) {
      showToast('Failed to update', 'error');
    }
  };

  const editingCard = editingId ? cards.find(c => c.id === editingId) : null;

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <AdminPage
      active="content"
      title="Content Cards"
      subtitle="Manage carousel content shown on coach and player dashboards"
      actions={<Button variant="accent" onClick={openCreate}>+ Add Card</Button>}
    >

      {/* Toast */}
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`}>
          {message}
        </div>
      )}

      {/* Table */}
      {cards.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--color-text-secondary)' }}>
          No content cards yet. Click "Add Card" to create one.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Title</th>
                <th style={thStyle}>Audience</th>
                <th style={thStyle}>Media</th>
                <th style={thStyle}>Color</th>
                <th style={thStyle}>Active</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cards.map(card => (
                <tr key={card.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={tdStyle}>{card.sort_order}</td>
                  <td style={{ ...tdStyle, maxWidth: 260 }}>
                    <div style={{ fontWeight: 500 }}>{card.title}</div>
                    <div style={{ display: 'block', fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
                      {card.preview}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500, background: audienceBadgeColor(card.target_audience), color: '#fff' }}>
                      {card.target_audience}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {card.media_type === 'video' && '🎬'}
                    {card.media_type === 'audio' && '🎧'}
                    {!card.media_type && '—'}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ display: 'inline-block', width: 20, height: 20, borderRadius: 4, border: '1px solid #ccc', background: hexToRgba(card.color) }} />
                  </td>
                  <td style={tdStyle}>
                    <Button
                      variant="ghost"
                      iconOnly
                      onClick={() => toggleActive(card)}
                      title={card.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                      style={{ fontSize: 18 }}
                    >
                      {card.is_active ? '✅' : '⬜'}
                    </Button>
                  </td>
                  <td style={tdStyle}>
                    <Button variant="secondary" size="sm" onClick={() => openEdit(card)} style={{ marginRight: 8 }}>Edit</Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(card.id)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2 className="modal-title">{editingId ? 'Edit Content Card' : 'New Content Card'}</h2>

            <div className="form-field">
              <label className="form-field__label">Title *</label>
              <input
                className="form-field__input"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="Card title"
              />
            </div>

            <div className="form-field">
              <label className="form-field__label">Preview Text *</label>
              <textarea
                className="form-field__input"
                rows={3}
                value={form.preview}
                onChange={e => setForm({ ...form, preview: e.target.value })}
                placeholder="Short text shown in the carousel card"
              />
            </div>

            <div className="form-field">
              <label className="form-field__label">Detail Text *</label>
              <textarea
                className="form-field__input"
                rows={5}
                value={form.detail}
                onChange={e => setForm({ ...form, detail: e.target.value })}
                placeholder="Full text shown when the card is tapped"
              />
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-field">
                <label className="form-field__label">Color</label>
                <select
                  className="form-field__select"
                  value={form.color}
                  onChange={e => setForm({ ...form, color: e.target.value })}
                >
                  {COLOR_PRESETS.map(p => (
                    <option key={p.hex} value={p.hex}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label className="form-field__label">Target Audience</label>
                <select
                  className="form-field__select"
                  value={form.target_audience}
                  onChange={e => setForm({ ...form, target_audience: e.target.value })}
                >
                  {AUDIENCE_OPTIONS.map(a => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-field">
                <label className="form-field__label">Sort Order</label>
                <input
                  className="form-field__input"
                  type="number"
                  min={0}
                  value={form.sort_order}
                  onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="form-field">
                <label className="form-field__label">Active</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={e => setForm({ ...form, is_active: e.target.checked })}
                  />
                  <span style={{ fontSize: 14 }}>Visible in carousel</span>
                </label>
              </div>
            </div>

            <div className="form-field">
              <label className="form-field__label">Media (Video or Audio)</label>
              {editingCard?.media_type && !removeMedia && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 10px', background: '#f1f5f9', borderRadius: 6, fontSize: 13 }}>
                  <span>{editingCard.media_type === 'video' ? '🎬' : '🎧'} {editingCard.media_type} attached</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setRemoveMedia(true)}
                    style={{ marginLeft: 'auto' }}
                  >
                    Remove
                  </Button>
                </div>
              )}
              {removeMedia && !mediaFile && (
                <div style={{ fontSize: 13, color: 'var(--color-danger)', marginBottom: 8 }}>
                  Media will be removed on save.{' '}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setRemoveMedia(false)}
                  >
                    Undo
                  </Button>
                </div>
              )}
              <input
                type="file"
                accept="video/*,audio/*"
                onChange={e => {
                  setMediaFile(e.target.files[0] || null);
                  if (e.target.files[0]) setRemoveMedia(false);
                }}
              />
              <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
                Max 1 GB. Video or audio files only.
              </p>
            </div>

            <div className="modal-actions">
              <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button variant="accent" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}

// Helpers
const thStyle = {
  padding: '10px 12px', fontWeight: 700, fontSize: 10.5,
  textTransform: 'uppercase', letterSpacing: '0.08em', color: '#8CA096',
};
const tdStyle = { padding: '11px 12px', verticalAlign: 'middle', color: '#4A5867' };

function audienceBadgeColor(audience) {
  switch (audience) {
    case 'coach': return '#ea580c';
    case 'player': return '#16a34a';
    case 'partner': return '#0d9488';
    default: return '#64748b';
  }
}

function hexToRgba(hex) {
  if (!hex || hex.length < 7) return '#f5f5f5';
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const a = hex.length >= 9 ? parseInt(hex.slice(7, 9), 16) / 255 : 1;
  return `rgba(${r},${g},${b},${a})`;
}
